import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide, forceX, forceY, forceManyBody } from 'd3-force';
import { getIndustryColor, getIndustryColorMap } from '../../utils/colorConstants';
import './NetworkGraph.css';

// Shared node-size scaling – sqrt compresses large counts while
// keeping small-vs-large clearly distinguishable.  All node types
// use the same formula: raw = proposal count from backend.
const scaleNodeSize = (raw) => 5 + Math.sqrt(raw || 1) * 5;

// Helper to convert hex color to RGBA
const hexToRgba = (hex, alpha = 1) => {
  const bigint = parseInt(hex.slice(1), 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Helper to parse node info (assuming it might be needed for labels)
const parseNodeInfo = (nodeId) => {
  let id = String(nodeId || '');
  // Strip schema-driven multilayer prefix (e.g. "row::", "cluster::",
  // "affiliation::", "author::", "category::") so labels show the clean value.
  const sep = id.indexOf('::');
  if (sep !== -1) id = id.slice(sep + 2);
  const typeSuffixes = {
    '_Affiliation': 'Affiliation',
    '_Organization': 'Organization',
    '_Beamline': 'Beamline'
  };
  let displayName = id;
  
  // Use the type information even though we only need the suffix to modify displayName
  // eslint-disable-next-line no-unused-vars
  for (const [suffix, type] of Object.entries(typeSuffixes)) {
    if (id.endsWith(suffix)) {
      displayName = id.substring(0, id.length - suffix.length);
      break;
    }
  }
  displayName = displayName.replace(/['"]/g, '').replace(/_/g, ' ');
  return displayName;
};

function NetworkGraph({
  graphData, // Use graphData directly
  onNodeSelect,
  selectedNodeIds, // Multi-select: Set of selected node IDs
  neighborNodeIds, // New prop
  themeColors,
  hideOrgLabels,
  setHideOrgLabels,
  minLinkWeight,      // Lifted to App.jsx
  setMinLinkWeight,   // Lifted to App.jsx
  maxLinkWeight,       // Computed in App.jsx
  onHelpSection,        // Contextual help callback
  showIndustryOverlay,  // Industry Aura overlay toggle
  setShowIndustryOverlay, // Industry Aura overlay setter
  theme,                 // Current theme name for industry colors
  networkMeta,           // {views, defaultView, peopleLayers, toggles} from /api/config (multilayer schemas)
  networkView,           // Active view id
  onNetworkViewChange,   // (viewId) => void; triggers re-analysis
  peopleAxis,            // 'affiliation' | 'author'
  onPeopleAxisChange,    // (axis) => void; triggers re-analysis
  requireCluster,        // bool
  onRequireClusterChange // (bool) => void; triggers re-analysis
}) {
  const graphRef = useRef();
  const containerRef = useRef();
  const needsInitialFit = useRef(true);
  const [dimensions, setDimensions] = useState({ width: 800, height: 600 });
  const [physicsEnabled, setPhysicsEnabled] = useState(true);
  const [chargeStrength, setChargeStrength] = useState(-30);
  const [collisionRadius, setCollisionRadius] = useState(0);
  const [centerStrength, setCenterStrength] = useState(0.05);
  const [showPhysicsPanel, setShowPhysicsPanel] = useState(false);
  const [layoutOptimising, setLayoutOptimising] = useState(false);
  const [industryOpacity, setIndustryOpacity] = useState(0.18); // 0..1 base alpha for aura center
  const [auraSpread, setAuraSpread] = useState(1.0); // 0.5..2 radius multiplier (1=petals, 2≈heatmap)

  // ── Node search state ──
  const [searchTerm, setSearchTerm] = useState('');
  const [searchHighlightId, setSearchHighlightId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchInputRef = useRef();
  const searchWrapperRef = useRef();
  const pulsePhase = useRef(0);
  const pulseRaf = useRef(null);

  // Use maxLinkWeight from props (renamed from local maxWeight)
  const maxWeight = maxLinkWeight;

  // Label for the aura overlay — uses schema industry col label when available
  const auraLabel = (networkMeta && networkMeta.auraLabel) || 'Industry';

  // Industries actually present in the current graph (for legend filtering)
  const activeIndustries = useMemo(() => {
    const seen = new Set();
    (graphData.nodes || []).forEach(n => {
      if (n.industryDistribution) {
        Object.keys(n.industryDistribution).forEach(ind => seen.add(ind));
      }
    });
    return seen;
  }, [graphData.nodes]);

  // Distinct node types present in the current graph, with a representative
  // colour + shape. Used to render a schema-agnostic legend. When the graph is
  // the MaxIV tripartite one (Organization/Beamline/Affiliation), we keep the
  // richer hand-tuned legend instead (see render below).
  const MAXIV_NODE_TYPES = useMemo(() => new Set(['Organization', 'Beamline', 'Affiliation']), []);
  const nodeTypeLegend = useMemo(() => {
    const byType = new Map();
    (graphData.nodes || []).forEach(n => {
      const t = n.type;
      if (!t || byType.has(t)) return;
      byType.set(t, {
        type: t,
        color: n.color || '#7F8C8D',
        shape: n.shape === 'square' ? 'square' : (n.shape === 'triangle' ? 'triangle' : 'circle'),
      });
    });
    return [...byType.values()];
  }, [graphData.nodes]);

  // True when the graph is schema-driven multilayer (MARVIN): node ids carry a
  // "layer::value" prefix. Used to avoid the hand-tuned MaxIV legend leaking
  // into single-layer views (e.g. Collaboration fabric = affiliations only).
  const isSchemaDriven = useMemo(
    () => (graphData.nodes || []).some(n => typeof n.id === 'string' && n.id.includes('::')),
    [graphData.nodes]
  );

  // True when the graph is the classic MaxIV tripartite structure.
  const isMaxIVLegend = useMemo(() => (
    !isSchemaDriven && nodeTypeLegend.length > 0 && nodeTypeLegend.every(e => MAXIV_NODE_TYPES.has(e.type))
  ), [isSchemaDriven, nodeTypeLegend, MAXIV_NODE_TYPES]);

  // ── Industry Aura: fixed industry → color mapping (theme-aware) ──
  // Pass activeIndustries so unknown names get a deterministic hash-based colour
  // rather than falling back to gray.
  const industryColorMap = useMemo(() => {
    return getIndustryColorMap(theme, [...activeIndustries]);
  }, [theme, activeIndustries]);

  // Filter graph data by minimum link weight, removing orphaned nodes
  const filteredGraphData = useMemo(() => {
    if (minLinkWeight <= 1) return graphData;
    const filteredLinks = graphData.links.filter(l => (l.weight || 1) >= minLinkWeight);
    const connectedNodeIds = new Set();
    filteredLinks.forEach(l => {
      const sId = typeof l.source === 'object' ? l.source.id : l.source;
      const tId = typeof l.target === 'object' ? l.target.id : l.target;
      connectedNodeIds.add(sId);
      connectedNodeIds.add(tId);
    });
    const filteredNodes = graphData.nodes.filter(n => connectedNodeIds.has(n.id));
    return { nodes: filteredNodes, links: filteredLinks };
  }, [graphData, minLinkWeight]);

  // Recompute neighbors based on filtered links so highlighting
  // matches the visible edges (not the full unfiltered set)
  const filteredNeighborIds = useMemo(() => {
    if (!selectedNodeIds || selectedNodeIds.size === 0) return new Set();
    const neighbors = new Set();
    filteredGraphData.links.forEach(l => {
      const sId = typeof l.source === 'object' ? l.source.id : l.source;
      const tId = typeof l.target === 'object' ? l.target.id : l.target;
      if (selectedNodeIds.has(sId)) neighbors.add(tId);
      if (selectedNodeIds.has(tId)) neighbors.add(sId);
    });
    // Remove selected nodes themselves from neighbors
    selectedNodeIds.forEach(id => neighbors.delete(id));
    return neighbors;
  }, [selectedNodeIds, filteredGraphData.links]);

  // Build lookup of node display names for autocomplete
  const nodeNameMap = useMemo(() => {
    const map = new Map();
    (filteredGraphData.nodes || []).forEach(n => {
      map.set(n.id, n.label || parseNodeInfo(n.id));
    });
    return map;
  }, [filteredGraphData.nodes]);

  // Autocomplete suggestions (max 12)
  const suggestions = useMemo(() => {
    if (!searchTerm || searchTerm.length < 1) return [];
    const lower = searchTerm.toLowerCase();
    const results = [];
    for (const [id, name] of nodeNameMap) {
      if (name.toLowerCase().includes(lower)) {
        results.push({ id, name });
        if (results.length >= 12) break;
      }
    }
    // Sort: starts-with first, then alphabetical
    results.sort((a, b) => {
      const aStarts = a.name.toLowerCase().startsWith(lower) ? 0 : 1;
      const bStarts = b.name.toLowerCase().startsWith(lower) ? 0 : 1;
      if (aStarts !== bStarts) return aStarts - bStarts;
      return a.name.localeCompare(b.name);
    });
    return results;
  }, [searchTerm, nodeNameMap]);

  // Select a search result – highlight & pan to node
  const handleSearchSelect = useCallback((nodeId) => {
    setSearchHighlightId(nodeId);
    setSearchTerm(nodeNameMap.get(nodeId) || '');
    setShowSuggestions(false);

    // Pan camera to the highlighted node
    if (graphRef.current) {
      const node = filteredGraphData.nodes.find(n => n.id === nodeId);
      if (node && typeof node.x === 'number' && typeof node.y === 'number') {
        graphRef.current.centerAt(node.x, node.y, 600);
        graphRef.current.zoom(2.5, 600);
      }
    }
  }, [nodeNameMap, filteredGraphData.nodes]);

  // Clear search
  const handleSearchClear = useCallback(() => {
    setSearchTerm('');
    setSearchHighlightId(null);
    setShowSuggestions(false);
  }, []);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchWrapperRef.current && !searchWrapperRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Pulse animation for search highlight ring
  useEffect(() => {
    if (searchHighlightId) {
      const animate = () => {
        pulsePhase.current = (pulsePhase.current + 0.04) % (Math.PI * 2);
        if (graphRef.current) {
          // Request a re-render by accessing the internal renderer
          const canvas = containerRef.current?.querySelector('canvas');
          if (canvas) canvas.dispatchEvent(new Event('_pulse'));
        }
        pulseRaf.current = requestAnimationFrame(animate);
      };
      pulseRaf.current = requestAnimationFrame(animate);
      return () => cancelAnimationFrame(pulseRaf.current);
    }
  }, [searchHighlightId]);

  // Clear search highlight when a node is clicked (selected)
  useEffect(() => {
    if (selectedNodeIds && selectedNodeIds.size > 0) {
      setSearchHighlightId(null);
      setSearchTerm('');
    }
  }, [selectedNodeIds]);

  // Capture network canvas as high-DPI PNG
  const handleCaptureScreenshot = useCallback(() => {
    if (!containerRef.current) return;
    const canvas = containerRef.current.querySelector('canvas');
    if (!canvas) return;

    // Scale factor: aim for 300 DPI equivalent
    // Browser canvas is ~96 DPI, so scale by ~3.125x
    const scaleFactor = 300 / 96;
    const srcW = canvas.width;
    const srcH = canvas.height;
    const dstW = Math.round(srcW * scaleFactor);
    const dstH = Math.round(srcH * scaleFactor);

    // Create offscreen canvas at target resolution
    const offscreen = document.createElement('canvas');
    offscreen.width = dstW;
    offscreen.height = dstH;
    const ctx = offscreen.getContext('2d');

    // White background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, dstW, dstH);

    // Draw current canvas scaled up
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(canvas, 0, 0, srcW, srcH, 0, 0, dstW, dstH);

    // ── Draw legend overlay ──────────────────────────────────────────
    // Scale legend relative to the output image size vs CSS display size
    // This accounts for both devicePixelRatio and DPI scaling
    const cssW = canvas.getBoundingClientRect().width || srcW;
    const s = dstW / cssW; // total scale: CSS → 300 DPI output
    const lx = 18 * s;    // legend x offset
    let ly = 18 * s;      // legend y start
    const lineH = 20 * s;
    const subIndent = 16 * s;
    const iconR = 6 * s;  // icon radius / half-size
    const fontSize = Math.round(11 * s);
    const subFontSize = Math.round(10 * s);

    // Semi-transparent background box. Height depends on whether we draw the
    // rich MaxIV legend or a generic one-row-per-type legend.
    const boxW = 160 * s;
    const boxH = (isMaxIVLegend ? 190 : (32 + nodeTypeLegend.length * 22)) * s;
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.strokeStyle = '#ccc';
    ctx.lineWidth = 1 * s;
    ctx.beginPath();
    ctx.roundRect(lx - 8 * s, ly - 8 * s, boxW, boxH, 6 * s);
    ctx.fill();
    ctx.stroke();

    const drawCircle = (x, y, r, color) => {
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
    };
    const drawTriangle = (x, y, r, color) => {
      const h = r * Math.sqrt(3);
      ctx.beginPath();
      ctx.moveTo(x, y - h / 2);
      ctx.lineTo(x - r, y + h / 2);
      ctx.lineTo(x + r, y + h / 2);
      ctx.closePath();
      ctx.fillStyle = color;
      ctx.fill();
    };
    const drawSquare = (x, y, r, color) => {
      ctx.fillStyle = color;
      ctx.fillRect(x - r, y - r, r * 2, r * 2);
    };
    const drawShape = (shape, x, y, r, color) => {
      if (shape === 'square') drawSquare(x, y, r, color);
      else if (shape === 'triangle') drawTriangle(x, y, r, color);
      else drawCircle(x, y, r, color);
    };

    ctx.textBaseline = 'middle';
    ctx.textAlign = 'left';

    if (!isMaxIVLegend) {
      // Generic, schema-driven legend: one row per node type present.
      ctx.font = `bold ${fontSize}px "Segoe UI", sans-serif`;
      for (const entry of nodeTypeLegend) {
        drawShape(entry.shape, lx + iconR, ly + iconR, iconR * 0.9, entry.color || '#7F8C8D');
        ctx.fillStyle = '#333';
        ctx.fillText(entry.type, lx + iconR * 2 + 8 * s, ly + iconR);
        ly += lineH;
      }
    } else {
    // Organisation header
    drawCircle(lx + iconR, ly + iconR, iconR, '#1c566b');
    ctx.fillStyle = '#333';
    ctx.font = `bold ${fontSize}px "Segoe UI", sans-serif`;
    ctx.fillText('Organisation', lx + iconR * 2 + 8 * s, ly + iconR);
    ly += lineH;

    // Sub-items
    const subItems = [
      { label: 'Company', color: '#2e86ab' },
      { label: 'University', color: '#a23b72' },
      { label: 'Other', color: '#8e7c68' },
    ];
    ctx.font = `${subFontSize}px "Segoe UI", sans-serif`;
    for (const sub of subItems) {
      drawCircle(lx + subIndent + iconR * 0.7, ly + iconR, iconR * 0.65, sub.color);
      ctx.fillStyle = '#444';
      ctx.fillText(sub.label, lx + subIndent + iconR * 2 + 4 * s, ly + iconR);
      ly += lineH * 0.85;
    }

    ly += lineH * 0.3;

    // Beamline
    ctx.font = `bold ${fontSize}px "Segoe UI", sans-serif`;
    drawCircle(lx + iconR, ly + iconR, iconR * 0.9, '#d67c29');
    ctx.fillStyle = '#333';
    ctx.fillText('Beamline', lx + iconR * 2 + 8 * s, ly + iconR);
    ly += lineH;

    // Affiliation
    drawCircle(lx + iconR, ly + iconR, iconR * 0.8, '#af4b3e');
    ctx.fillStyle = '#333';
    ctx.fillText('Affiliation', lx + iconR * 2 + 8 * s, ly + iconR);
    }

    // ── Draw Industry Aura legend (bottom-left) when overlay is active ──
    if (showIndustryOverlay && activeIndustries.size > 0) {
      const activeEntries = Object.entries(industryColorMap)
        .filter(([industry]) => activeIndustries.has(industry));

      if (activeEntries.length > 0) {
        const ilLineH = 18 * s;
        const ilSwatchR = 5 * s;
        const ilPadX = 10 * s;
        const ilPadY = 8 * s;
        const ilFontSize = Math.round(10 * s);
        const ilHeaderFontSize = Math.round(11 * s);

        // Measure text widths to determine box width
        ctx.font = `bold ${ilHeaderFontSize}px "Segoe UI", sans-serif`;
        let ilMaxTextW = ctx.measureText('Industry Aura').width;
        ctx.font = `${ilFontSize}px "Segoe UI", sans-serif`;
        for (const [industry] of activeEntries) {
          const tw = ctx.measureText(industry).width;
          if (tw > ilMaxTextW) ilMaxTextW = tw;
        }

        const ilBoxW = ilMaxTextW + ilSwatchR * 2 + 8 * s + ilPadX * 2 + 4 * s;
        const ilBoxH = ilPadY * 2 + ilLineH + activeEntries.length * ilLineH;
        const ilX = 18 * s;
        const ilY = dstH - ilBoxH - 18 * s;

        // Semi-transparent background
        ctx.fillStyle = 'rgba(255,255,255,0.88)';
        ctx.strokeStyle = '#ccc';
        ctx.lineWidth = 1 * s;
        ctx.beginPath();
        ctx.roundRect(ilX, ilY, ilBoxW, ilBoxH, 6 * s);
        ctx.fill();
        ctx.stroke();

        // Header
        let ilCurY = ilY + ilPadY;
        ctx.fillStyle = '#333';
        ctx.font = `bold ${ilHeaderFontSize}px "Segoe UI", sans-serif`;
        ctx.textBaseline = 'top';
        ctx.textAlign = 'left';
        ctx.fillText(`${auraLabel} Aura`, ilX + ilPadX, ilCurY);
        ilCurY += ilLineH;

        // Color swatches
        ctx.font = `${ilFontSize}px "Segoe UI", sans-serif`;
        for (const [industry, color] of activeEntries) {
          // Swatch circle
          ctx.beginPath();
          ctx.arc(ilX + ilPadX + ilSwatchR, ilCurY + ilLineH / 2, ilSwatchR, 0, Math.PI * 2);
          ctx.fillStyle = color;
          ctx.fill();

          // Label
          ctx.fillStyle = '#444';
          ctx.fillText(industry, ilX + ilPadX + ilSwatchR * 2 + 8 * s, ilCurY + 1 * s);
          ilCurY += ilLineH;
        }

        // Reset textBaseline for consistency
        ctx.textBaseline = 'middle';
      }
    }

    // Download – patch PNG pHYs chunk to declare 300 DPI
    offscreen.toBlob(async (blob) => {
      if (!blob) return;
      const buf = new Uint8Array(await blob.arrayBuffer());

      // PNG pHYs chunk: sets pixels-per-unit to 11811 px/m ≈ 300 DPI
      // Chunk: length(4) + "pHYs"(4) + ppuX(4) + ppuY(4) + unit(1) + CRC(4) = 21 bytes
      const ppm = 11811; // Math.round(300 / 0.0254)
      const phys = new Uint8Array(21);
      const dv = new DataView(phys.buffer);
      dv.setUint32(0, 9); // data length
      phys.set([0x70, 0x48, 0x59, 0x73], 4); // "pHYs"
      dv.setUint32(8, ppm);   // X pixels per unit
      dv.setUint32(12, ppm);  // Y pixels per unit
      phys[16] = 1;           // unit = metre

      // CRC32 over type + data (bytes 4..16)
      const crcTable = (() => {
        const t = new Uint32Array(256);
        for (let n = 0; n < 256; n++) {
          let c = n;
          for (let k = 0; k < 8; k++) c = (c & 1) ? (0xedb88320 ^ (c >>> 1)) : (c >>> 1);
          t[n] = c;
        }
        return t;
      })();
      let crc = 0xffffffff;
      for (let i = 4; i <= 16; i++) crc = crcTable[(crc ^ phys[i]) & 0xff] ^ (crc >>> 8);
      dv.setUint32(17, (crc ^ 0xffffffff) >>> 0);

      // Find insertion point: right after IHDR chunk (signature 8 bytes + IHDR 25 bytes = byte 33)
      // Also strip any existing pHYs chunk
      const out = [];
      out.push(buf.slice(0, 33)); // PNG sig + IHDR
      out.push(phys);             // our pHYs

      // Walk remaining chunks, skip existing pHYs
      let pos = 33;
      while (pos < buf.length) {
        const chunkLen = (buf[pos] << 24 | buf[pos+1] << 16 | buf[pos+2] << 8 | buf[pos+3]) >>> 0;
        const chunkType = String.fromCharCode(buf[pos+4], buf[pos+5], buf[pos+6], buf[pos+7]);
        const totalLen = chunkLen + 12; // length(4) + type(4) + data + crc(4)
        if (chunkType !== 'pHYs') {
          out.push(buf.slice(pos, pos + totalLen));
        }
        pos += totalLen;
      }

      const finalBlob = new Blob(out, { type: 'image/png' });
      const url = URL.createObjectURL(finalBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'network_screenshot.png';
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    }, 'image/png');
  }, [showIndustryOverlay, activeIndustries, industryColorMap, isMaxIVLegend, nodeTypeLegend]);

  // Removed graphData preparation logic, assuming it's passed correctly

  // Update dimensions when container size changes
  useEffect(() => {
    function updateDimensions() {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.offsetWidth,
          height: containerRef.current.offsetHeight
        });
      }
    }

    // Initial update
    updateDimensions();

    // Add resize listener
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    // Cleanup
    return () => {
      if (containerRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        resizeObserver.unobserve(containerRef.current);
      }
    };
  }, []);

  // Apply charge strength to force simulation (FA2-like: degree-proportional)
  useEffect(() => {
    if (graphRef.current) {
      // Build degree map so hubs repel more strongly (ForceAtlas2 behaviour)
      const degreeMap = new Map();
      filteredGraphData.nodes?.forEach(n => degreeMap.set(n.id, 0));
      (filteredGraphData.links || []).forEach(l => {
        const sId = typeof l.source === 'object' ? l.source.id : l.source;
        const tId = typeof l.target === 'object' ? l.target.id : l.target;
        degreeMap.set(sId, (degreeMap.get(sId) || 0) + 1);
        degreeMap.set(tId, (degreeMap.get(tId) || 0) + 1);
      });
      graphRef.current.d3Force('charge').strength(node => {
        const deg = degreeMap.get(node.id) || 1;
        return chargeStrength * (1 + deg * 0.25);
      });
      graphRef.current.d3ReheatSimulation();
    }
  }, [chargeStrength, filteredGraphData]);

  // Apply collision radius to push nodes/labels apart
  useEffect(() => {
    if (graphRef.current) {
      if (collisionRadius > 0) {
        graphRef.current.d3Force('collide',
          forceCollide()
            .radius(node => scaleNodeSize(node.size) / 2 + collisionRadius)
            .strength(0.8)
            .iterations(4)
        );
      } else {
        graphRef.current.d3Force('collide', null);
      }
      graphRef.current.d3ReheatSimulation();
    }
  }, [collisionRadius]);

  // Apply center pull strength to bring islands together
  useEffect(() => {
    if (graphRef.current) {
      if (centerStrength > 0) {
        graphRef.current.d3Force('x', forceX().strength(centerStrength));
        graphRef.current.d3Force('y', forceY().strength(centerStrength));
      } else {
        graphRef.current.d3Force('x', null);
        graphRef.current.d3Force('y', null);
      }
      graphRef.current.d3ReheatSimulation();
    }
  }, [centerStrength]);

  // Clear the optimising flag after a short delay
  const layoutOptimisingTimerRef = useRef(null);

  // Adaptive physics — structure-aware auto-tuning.
  //
  // Instead of guessing from node count alone, we measure the graph's actual
  // topology (size, connectivity, density, fragmentation) and derive gravity
  // and island-pull from those properties. The intuition:
  //   • Gravity (repulsion): scales with size + density. Dense, hub-heavy
  //     graphs collapse inward, so they need stronger repulsion to breathe.
  //   • Island Pull (centre gravity): driven by *fragmentation* — the number
  //     of disconnected components relative to size. A graph that shatters
  //     into many islands/dyads (e.g. author co-authorship) needs a strong
  //     pull to keep loose ends on-screen; one connected blob needs almost
  //     none (zoom-to-fit frames it anyway).
  //   • Link distance: denser neighbourhoods get more breathing room.
  // Label Spacing (collision) is intentionally NOT auto-tuned — it's a
  // presentation preference left entirely to the user.
  useEffect(() => {
    const nodes = filteredGraphData.nodes || [];
    const links = filteredGraphData.links || [];
    const N = nodes.length;
    if (N === 0) return;
    const E = links.length;

    const linkEnds = (l) => [
      typeof l.source === 'object' ? l.source.id : l.source,
      typeof l.target === 'object' ? l.target.id : l.target,
    ];

    // --- Topology metrics ---
    const avgDeg = (2 * E) / N;                          // mean connections per node
    const maxPairs = N > 1 ? (N * (N - 1)) / 2 : 1;
    const density = Math.min(1, E / maxPairs);           // 0 (sparse) .. 1 (clique)

    // Connected components via union-find → fragmentation
    const parent = new Map();
    nodes.forEach(n => parent.set(n.id, n.id));
    const find = (x) => {
      while (parent.get(x) !== x) { parent.set(x, parent.get(parent.get(x))); x = parent.get(x); }
      return x;
    };
    links.forEach(l => {
      const [s, t] = linkEnds(l);
      if (!parent.has(s)) parent.set(s, s);
      if (!parent.has(t)) parent.set(t, t);
      const rs = find(s), rt = find(t);
      if (rs !== rt) parent.set(rs, rt);
    });
    const roots = new Set();
    nodes.forEach(n => roots.add(find(n.id)));
    const components = roots.size;
    const fragmentation = components / N;                // 0 (one blob) .. ~1 (all isolated)

    // Optional per-view fine-tune multipliers (default 1 — algorithm leads).
    let hints = null;
    if (networkMeta?.views && networkView) {
      const viewDef = networkMeta.views.find(v => v.id === networkView);
      hints = viewDef?.physics_hints?.[peopleAxis || 'affiliation'] || null;
    }
    const chargeMult   = hints?.chargeMultiplier ?? 1;
    const centerMult   = hints?.centerStrengthMultiplier ?? 1;
    const linkDistMult = hints?.linkDistanceMultiplier ?? 1;

    // --- Gravity / repulsion: grows with size, avg degree and density ---
    const baseCharge = 40 + N * 0.5 + avgDeg * 14 + density * 220;
    const adaptiveCharge = -Math.round(Math.min(600, baseCharge * chargeMult));
    setChargeStrength(adaptiveCharge);

    // --- Island Pull: small fixed safety-net so nodes don't drift off to
    // infinity before zoom-to-fit fires.  We deliberately avoid scaling with
    // fragmentation — high-fragmentation graphs (e.g. author co-authorship)
    // would otherwise auto-max this and compress the layout too tightly.
    // The schema hints multiplier still applies, so per-view tuning works.
    // zoom-to-fit handles framing; this just keeps islands on the canvas.
    const adaptiveCenter = Math.min(1.0, Math.max(0.04, 0.05 * centerMult));
    setCenterStrength(parseFloat(adaptiveCenter.toFixed(2)));

    // --- Link forces: log-distance + weight-based strength ---
    if (graphRef.current) {
      const degreeMap = new Map();
      nodes.forEach(n => degreeMap.set(n.id, 0));
      links.forEach(l => {
        const [s, t] = linkEnds(l);
        degreeMap.set(s, (degreeMap.get(s) || 0) + 1);
        degreeMap.set(t, (degreeMap.get(t) || 0) + 1);
      });
      const wMax = Math.max(1, ...links.map(l => l.weight || 1));

      const linkForce = graphRef.current.d3Force('link');
      if (linkForce) {
        // LinLog-like: logarithmic distance dampens hub-to-hub attraction so
        // clusters spread out; denser graphs get proportionally more room.
        const distScale = 1 + Math.min(1.5, density * 8);
        linkForce.distance(link => {
          const [s, t] = linkEnds(link);
          const sDeg = degreeMap.get(s) || 1;
          const tDeg = degreeMap.get(t) || 1;
          return (30 + Math.log(1 + sDeg + tDeg) * 25) * distScale * linkDistMult;
        });
        // Weight-based strength: strong intra-cluster links pull tight, weak
        // inter-cluster links barely pull — improves cluster separation.
        linkForce.strength(link => {
          const w = (link.weight || 1) / wMax;
          const [s, t] = linkEnds(link);
          const sDeg = degreeMap.get(s) || 1;
          const tDeg = degreeMap.get(t) || 1;
          return w / Math.min(sDeg, tDeg);
        });
      }
      graphRef.current.d3ReheatSimulation();
    }

    // Flag for initial zoom-to-fit after layout stabilises
    needsInitialFit.current = true;

    // Show the optimising notice when the graph is non-trivial enough that
    // auto-tuning meaningfully kicks in (large or fragmented).
    if (N > 60 || fragmentation > 0.15) {
      setLayoutOptimising(true);
      clearTimeout(layoutOptimisingTimerRef.current);
      layoutOptimisingTimerRef.current = setTimeout(() => setLayoutOptimising(false), 4000);
    }
  }, [filteredGraphData, networkView, peopleAxis, networkMeta]);

  // Toggle physics (freeze/unfreeze) - only stop the simulation, keep rendering alive
  useEffect(() => {
    if (graphRef.current) {
      if (physicsEnabled) {
        // Unpin all nodes and restart simulation
        filteredGraphData.nodes.forEach(node => {
          node.fx = undefined;
          node.fy = undefined;
        });
        graphRef.current.d3ReheatSimulation();
      } else {
        // Pin every node at its current position to freeze layout
        filteredGraphData.nodes.forEach(node => {
          node.fx = node.x;
          node.fy = node.y;
        });
      }
    }
  }, [physicsEnabled, filteredGraphData.nodes]);

  // Effect to zoom/center when selectedNodeIds changes
  useEffect(() => {
    if (selectedNodeIds && selectedNodeIds.size > 0 && graphRef.current) {
      // Center on the last selected node (most recently added)
      const lastId = Array.from(selectedNodeIds).pop();
      const node = filteredGraphData.nodes.find(n => n.id === lastId);
      if (node && typeof node.x === 'number' && typeof node.y === 'number') {
        // Offset center leftward to account for the NodeDetail panel (~350px)
        // Convert pixel offset to graph-space using current zoom
        const currentZoom = graphRef.current.zoom() || 1;
        const panelOffsetPx = 175; // half the ~350px panel width
        const offsetX = panelOffsetPx / currentZoom;
        graphRef.current.centerAt(node.x - offsetX, node.y, 800);
      }
    }
  }, [selectedNodeIds, filteredGraphData.nodes]);

  // Function to determine node color based on type and theme
  const getNodeColor = useCallback((node) => {
    const colors = themeColors.nodes; // Use theme colors
    // Schema-driven multilayer nodes (ids like "affiliation::Foo") carry their
    // own backend colour; honour it directly so types that collide with the
    // MaxIV vocabulary (e.g. "Affiliation") aren't recoloured by the switch below.
    if (typeof node.id === 'string' && node.id.includes('::')) {
      return node.color || colors.default || '#7F8C8D';
    }
    switch (node.type) { // node.type is 'Organization', 'Beamline', or 'Affiliation'
      case 'Organization':
        // Use org subtype color if available
        switch (node.orgSubType) {
          case 'Company':
            return colors.orgCompany || colors.organization || '#F39C12';
          case 'University':
            return colors.orgUniversity || colors.organization || '#F39C12';
          case 'Other':
            return colors.orgOther || colors.organization || '#F39C12';
          case 'Unspecified':
            return colors.orgUnspecified || '#b8a994';
          default:
            return colors.organization || '#F39C12';
        }
      case 'Beamline':
        return colors.beamline; // Ensure this key exists in themeColors.nodes (e.g., from CSS var --color-nodes-beamline)
      case 'Affiliation':
        return colors.affiliation; // Ensure this key exists in themeColors.nodes (e.g., from CSS var --color-nodes-affiliation)
      default:
        // Schema-driven networks (e.g. MARVIN Cluster/Paper) supply their own
        // per-node colour from the backend; honour it before falling back.
        return node.color || colors.default || '#7F8C8D';
    }
  }, [themeColors]); // Depend on themeColors

  // Custom node rendering function with highlighting
  const handleNodeCanvasObject = useCallback((node, ctx, globalScale) => {
    const label = node.label || parseNodeInfo(node.id); // prefer clean backend label
    const rawSize = node.size || 5;
    // Wider linear spread so small vs large nodes are clearly different
    const nodeSize = scaleNodeSize(rawSize);
    const baseFontSize = 12;
    const scaleThresholdForLabels = 1.2;
    const largeNodeSizeThreshold = 15;
    const fontSize = (baseFontSize / globalScale) * (1 + Math.log1p(nodeSize) / 15);
    const shapeSize = Math.max(4, nodeSize / globalScale);

    const nodeBaseColor = getNodeColor(node);

    // --- Highlighting Logic ---
    const isSelected = selectedNodeIds && selectedNodeIds.has(node.id);
    const isNeighbor = filteredNeighborIds.has(node.id);
    const isSearchHighlighted = node.id === searchHighlightId;
    const isDimmedBySelection = selectedNodeIds && selectedNodeIds.size > 0 && !isSelected && !isNeighbor;
    const isDimmedBySearch = searchHighlightId !== null && !isSearchHighlighted;
    const isDimmed = isDimmedBySelection || isDimmedBySearch;

    ctx.save(); // Save context state

    // ── Industry Aura overlay (drawn BEFORE shape so it sits behind) ──
    if (showIndustryOverlay && node.industryDistribution && !isDimmed
        && typeof node.x === 'number' && typeof node.y === 'number'
        && isFinite(node.x) && isFinite(node.y)) {
      const dist = node.industryDistribution;
      const total = Object.values(dist).reduce((a, b) => a + b, 0);
      if (total > 0) {
        // Aura radius scales with node size × user spread multiplier
        const baseRadius = (scaleNodeSize(rawSize) * 2.8 + 12) / globalScale;
        const auraRadius = baseRadius * auraSpread;

        if (auraRadius > 0 && isFinite(auraRadius)) {
          // Sort industries by count ascending so the dominant one is drawn last (on top)
          const sorted = Object.entries(dist).sort((a, b) => a[1] - b[1]).slice(-6);

          // "Petal" approach: each industry gets a full radial-gradient circle
          // whose center is offset outward along its angular slice midpoint.
          // As spread increases the offset fraction shrinks → more concentric → heatmap feel.
          const offsetFraction = 0.3 / Math.max(1, auraSpread * 0.8); // petals converge at high spread
          let angleAccum = -Math.PI / 2; // start at 12-o'clock

          for (const [industry, count] of sorted) {
            const proportion = count / total;
            const color = industryColorMap[industry] || '#adb5bd';

            // Angular midpoint for this industry's "petal"
            const sweepAngle = proportion * Math.PI * 2;
            const midAngle = angleAccum + sweepAngle / 2;
            angleAccum += sweepAngle;

            // Offset center outward — more industries = more separation
            const offset = auraRadius * offsetFraction * (1 - proportion * 0.5);
            const cx = node.x + Math.cos(midAngle) * offset;
            const cy = node.y + Math.sin(midAngle) * offset;

            // Peak alpha: user intensity × boosted proportion (sqrt to lift small shares)
            const a = industryOpacity * Math.sqrt(proportion);

            const gradient = ctx.createRadialGradient(
              cx, cy, 0,
              cx, cy, auraRadius
            );
            // As spread grows, flatten the gradient so colour holds longer → heatmap feel
            // t goes from 0 (spread=0.5) to 1 (spread=2), controls how uniform the fill is
            const t = Math.min(1, (auraSpread - 0.5) / 1.5);
            gradient.addColorStop(0, hexToRgba(color, a));
            gradient.addColorStop(0.35 + t * 0.25, hexToRgba(color, a * (0.7 + t * 0.2)));
            gradient.addColorStop(0.7 + t * 0.15, hexToRgba(color, a * (0.2 + t * 0.15)));
            gradient.addColorStop(1, hexToRgba(color, 0));

            ctx.fillStyle = gradient;
            ctx.beginPath();
            ctx.arc(cx, cy, auraRadius, 0, Math.PI * 2);
            ctx.fill();
          }
        }
      }
    }

    if (isDimmed) {
      ctx.globalAlpha = 0.15; // Dim non-selected/non-neighbor nodes
    } else if (showIndustryOverlay) {
      // Crossfade: as aura intensity rises, nodes fade out
      // At default 0.18 → node alpha ~0.85; at 1.0 → node alpha ~0.05
      ctx.globalAlpha = Math.max(0.05, 1 - industryOpacity * 0.95);
    } else {
      ctx.globalAlpha = 1.0; // Ensure full opacity for selected/neighbors
    }

    // --- Draw Node Shape (all nodes are circles) ---
    ctx.fillStyle = nodeBaseColor;
    ctx.beginPath();
    ctx.arc(node.x, node.y, shapeSize / 2, 0, 2 * Math.PI, false);
    ctx.fill();

    // Optional: Add outline for selected node
    if (isSelected) {
        ctx.lineWidth = 2 / globalScale;
        ctx.strokeStyle = 'rgba(255, 255, 0, 0.8)'; // Yellow outline
        ctx.stroke();
    }

    // Search highlight – pulsing ring using theme teal
    if (isSearchHighlighted) {
      const pulse = 0.5 + 0.5 * Math.sin(pulsePhase.current);
      const ringRadius = shapeSize / 2 + (6 + pulse * 4) / globalScale;
      const tealColor = themeColors.primary?.teal || '#028fa3';
      ctx.beginPath();
      ctx.arc(node.x, node.y, ringRadius, 0, Math.PI * 2);
      ctx.lineWidth = (2.5 + pulse * 1.5) / globalScale;
      ctx.strokeStyle = hexToRgba(tealColor, 0.5 + pulse * 0.4);
      ctx.stroke();
      // Inner glow
      ctx.beginPath();
      ctx.arc(node.x, node.y, ringRadius + 2 / globalScale, 0, Math.PI * 2);
      ctx.lineWidth = 1 / globalScale;
      ctx.strokeStyle = hexToRgba(tealColor, 0.15 + pulse * 0.15);
      ctx.stroke();
    }

    // --- Refined Conditional Label Drawing ---
    const shouldHideLabel = hideOrgLabels;
    if (!shouldHideLabel && !isDimmed && (nodeSize >= largeNodeSizeThreshold || globalScale > scaleThresholdForLabels)) {
      ctx.font = `${fontSize}px Sans-Serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';

      // Adjust text position slightly based on shape for better centering below
      let textYOffset = shapeSize * 0.6; // Base offset similar to circle radius
      if (node.type === 'Beamline') {
         textYOffset = shapeSize * Math.sqrt(3) / 4 + fontSize * 0.2; // Adjust for triangle bottom
      } else if (node.type === 'Affiliation') {
         textYOffset = shapeSize * 0.6 + fontSize * 0.2; // Adjust for square bottom
      }
      const textY = node.y + textYOffset + fontSize * 0.6; // Position below the shape

      // Grey outline
      ctx.strokeStyle = '#555';
      ctx.lineWidth = 1 / globalScale;
      ctx.strokeText(label, node.x, textY);

      // Text fill with low alpha based on node color
      ctx.fillStyle = hexToRgba(nodeBaseColor, 0.75);
      ctx.fillText(label, node.x, textY);
    }

    ctx.restore(); // Restore context state (including globalAlpha)

  }, [getNodeColor, selectedNodeIds, filteredNeighborIds, hideOrgLabels, searchHighlightId, themeColors, showIndustryOverlay, industryColorMap, industryOpacity, auraSpread]); // Add dependencies

  // Link color function with highlighting
  const getLinkColor = useCallback((link) => {
    const defaultColor = themeColors.links?.default || '#BDC3C7'; // Greyish default
    const highlightColor = themeColors.links?.highlight || '#f39c12'; // Orange highlight

    // Ensure source/target IDs are correctly accessed
    const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
    const targetId = typeof link.target === 'object' ? link.target.id : link.target;

    const isConnectedToSelected = selectedNodeIds && selectedNodeIds.size > 0 && (selectedNodeIds.has(sourceId) || selectedNodeIds.has(targetId));

    // Dim all links when search highlight is active
    if (searchHighlightId) {
      const isConnectedToSearch = sourceId === searchHighlightId || targetId === searchHighlightId;
      return isConnectedToSearch
        ? hexToRgba(highlightColor, 0.6)
        : hexToRgba(defaultColor, 0.04);
    }

    if (!selectedNodeIds || selectedNodeIds.size === 0) {
      // No selection, use default color
      return hexToRgba(defaultColor, 0.4); // Slightly transparent default
    } else if (isConnectedToSelected) {
      // Connected to selected node, use highlight color
      return hexToRgba(highlightColor, 0.8);
    } else {
      // Not connected to selected node, use very faded default
      return hexToRgba(defaultColor, 0.05);
    }
  }, [selectedNodeIds, searchHighlightId, themeColors]);

  // Link width function with highlighting
  const getLinkWidth = useCallback((link) => {
      const defaultWidth = 1;
      const highlightWidth = 2;

      const sourceId = typeof link.source === 'object' ? link.source.id : link.source;
      const targetId = typeof link.target === 'object' ? link.target.id : link.target;
      const isConnectedToSelected = selectedNodeIds && selectedNodeIds.size > 0 && (selectedNodeIds.has(sourceId) || selectedNodeIds.has(targetId));

      return selectedNodeIds && selectedNodeIds.size > 0 && isConnectedToSelected ? highlightWidth : defaultWidth;
  }, [selectedNodeIds]);

  return (
    <div className="network-graph-container" ref={containerRef}>
      <div className="graph-wrapper">
        {/* ── Network toolbar: search + view controls in one compact row ── */}
        <div className="network-toolbar">
          {/* Search */}
          <div className="node-search-wrapper" ref={searchWrapperRef}>
            <div className="node-search-input-row">
              <span className="node-search-icon">🔍</span>
              <input
                ref={searchInputRef}
                type="text"
                className="node-search-input"
                placeholder="Search node…"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowSuggestions(true);
                  if (!e.target.value) setSearchHighlightId(null);
                }}
                onFocus={() => searchTerm && setShowSuggestions(true)}
                onKeyDown={(e) => {
                  if (e.key === 'Escape') {
                    handleSearchClear();
                    searchInputRef.current?.blur();
                  } else if (e.key === 'Enter' && suggestions.length > 0) {
                    handleSearchSelect(suggestions[0].id);
                  }
                }}
              />
              {searchTerm && (
                <button className="node-search-clear" onClick={handleSearchClear} title="Clear search">
                  ✕
                </button>
              )}
            </div>
            {showSuggestions && suggestions.length > 0 && (
              <ul className="node-search-suggestions">
                {suggestions.map(s => {
                  const node = filteredGraphData.nodes.find(n => n.id === s.id);
                  const typeLabel = node?.type || '';
                  return (
                    <li
                      key={s.id}
                      className="node-search-suggestion-item"
                      onMouseDown={() => handleSearchSelect(s.id)}
                    >
                      <span
                        className="node-search-suggestion-dot"
                        style={{ backgroundColor: node ? getNodeColor(node) : '#999' }}
                      />
                      <span className="node-search-suggestion-name">{s.name}</span>
                      <span className="node-search-suggestion-type">{typeLabel}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          {/* View controls inline (multilayer schemas only — MARVIN, RIANA, …).
              Views render as a tab strip; the axis toggle only appears on views
              that use the 'people' placeholder; all labels come from the schema. */}
          {networkMeta?.views?.length > 0 && (() => {
            const activeView = networkMeta.views.find(v => v.id === networkView)
              || networkMeta.views.find(v => v.id === networkMeta.defaultView)
              || networkMeta.views[0];
            const peopleLayers = (networkMeta.peopleLayers || []).map(p =>
              typeof p === 'string' ? { id: p, label: p.charAt(0).toUpperCase() + p.slice(1) } : p
            );
            const viewUsesPeople = !activeView?.layers || activeView.layers.includes('people');
            const showPeopleAxis = !!networkMeta.toggles?.peopleAxis && peopleLayers.length > 1 && viewUsesPeople;
            const activeAxis = peopleLayers.some(p => p.id === peopleAxis) ? peopleAxis : peopleLayers[0]?.id;
            const showRequireCluster = !!networkMeta.toggles?.requireCluster;
            const requireLabel = networkMeta.toggles?.requireLabel || 'Require cluster';
            return (
              <div className="network-view-controls">
                <div className="network-toolbar-sep" />
                <div className="network-view-tabs" role="tablist" aria-label="Network view">
                  {networkMeta.views.map(v => (
                    <button
                      key={v.id}
                      type="button"
                      role="tab"
                      aria-selected={v.id === activeView?.id}
                      className={`network-view-tab${v.id === activeView?.id ? ' active' : ''}`}
                      title={v.desc}
                      onClick={() => v.id !== activeView?.id && onNetworkViewChange && onNetworkViewChange(v.id)}
                    >
                      {v.name}
                    </button>
                  ))}
                </div>
                {showPeopleAxis && (
                  <>
                    <div className="network-toolbar-sep" />
                    <span className="network-view-label">{networkMeta.peopleLabel || 'People'}</span>
                    <div className="network-view-segment">
                      {peopleLayers.map(p => (
                        <button
                          key={p.id}
                          type="button"
                          className={activeAxis === p.id ? 'active' : ''}
                          onClick={() => activeAxis !== p.id && onPeopleAxisChange && onPeopleAxisChange(p.id)}
                        >{p.label}</button>
                      ))}
                    </div>
                  </>
                )}
                {showRequireCluster && (
                  <>
                    <div className="network-toolbar-sep" />
                    <label className="network-view-checkbox">
                      <input
                        type="checkbox"
                        checked={!!requireCluster}
                        onChange={(e) => onRequireClusterChange && onRequireClusterChange(e.target.checked)}
                      />
                      <span>{requireLabel}</span>
                    </label>
                  </>
                )}
              </div>
            );
          })()}
        </div>
        {/* Active-view caption (what am I looking at?) */}
        {networkMeta?.views?.length > 0 && (() => {
          const activeView = networkMeta.views.find(v => v.id === networkView)
            || networkMeta.views.find(v => v.id === networkMeta.defaultView)
            || networkMeta.views[0];
          return activeView?.desc ? (
            <div className="network-view-caption" key={activeView.id}>{activeView.desc}</div>
          ) : null;
        })()}
        {/* Legend */}
        <div className="graph-legend">
          {isMaxIVLegend ? (
            <>
              <div className="legend-group">
                <div className="legend-group-header">
                  <span className="legend-shape legend-shape-circle organization-color"></span>
                  <span>Organisation</span>
                </div>
                <div className="legend-subitem">
                  <span className="legend-shape legend-shape-circle legend-shape-small company-color"></span>
                  <span>Company</span>
                </div>
                <div className="legend-subitem">
                  <span className="legend-shape legend-shape-circle legend-shape-small university-color"></span>
                  <span>University</span>
                </div>
                <div className="legend-subitem">
                  <span className="legend-shape legend-shape-circle legend-shape-small other-org-color"></span>
                  <span>Other</span>
                </div>
              </div>
              <div className="legend-item">
                <span className="legend-shape legend-shape-circle beamline-color"></span>
                <span>Beamline</span>
              </div>
              <div className="legend-item">
                <span className="legend-shape legend-shape-circle affiliation-color"></span>
                <span>Affiliation</span>
              </div>
            </>
          ) : (
            nodeTypeLegend.map((entry) => (
              <div className="legend-item" key={entry.type}>
                <span
                  className={`legend-shape legend-shape-${entry.shape}`}
                  style={entry.shape === 'triangle'
                    ? { borderBottomColor: entry.color }
                    : { backgroundColor: entry.color }}
                ></span>
                <span>{entry.type}</span>
              </div>
            ))
          )}
        </div>

        {/* Layout-optimising notice */}
        {layoutOptimising && (
          <div className="layout-optimising-notice">
            <span className="layout-optimising-spinner" />
            <span className="layout-optimising-text">
              <strong>Optimising layout…</strong>
              <span>Fine-tune anytime via the&nbsp;⚙️&nbsp;gear icon</span>
            </span>
          </div>
        )}

        {/* Graph Controls */}
        <div className="graph-controls">
          <button
            className="graph-control-btn icon-only"
            onClick={handleCaptureScreenshot}
            title="Screenshot — save as high-res PNG"
          >
            📷
          </button>

          <button
            className={`graph-control-btn icon-only${hideOrgLabels ? ' active' : ''}`}
            onClick={() => setHideOrgLabels(prev => !prev)}
            title={hideOrgLabels ? 'Show labels' : 'Hide labels'}
          >
            🏷️
          </button>

          <button
            className={`graph-control-btn icon-only${showIndustryOverlay ? ' active' : ''}`}
            onClick={() => setShowIndustryOverlay(prev => !prev)}
            title={showIndustryOverlay ? `Hide ${auraLabel} Aura` : `Show ${auraLabel} Aura`}
          >
            🔬
          </button>

          <div className="physics-control-wrapper">
            <button
              className={`graph-control-btn icon-only${showPhysicsPanel ? ' active' : ''}`}
              onClick={() => setShowPhysicsPanel(prev => !prev)}
              title="Graph settings"
            >
              ⚙️
            </button>

            {showPhysicsPanel && (
              <div className="physics-panel">
                <div className="physics-panel-header">Physics Controls</div>
                <div className="physics-option">
                  <label className="physics-toggle-label">
                    <span>Simulation</span>
                    <button
                      className={`physics-toggle-btn${physicsEnabled ? ' on' : ' off'}`}
                      onClick={() => setPhysicsEnabled(prev => !prev)}
                    >
                      {physicsEnabled ? 'ON' : 'OFF'}
                    </button>
                  </label>
                </div>
                <div className="physics-option">
                  <label className="physics-slider-label">
                    <span>Gravity</span>
                    <span className="physics-slider-value">{chargeStrength}</span>
                  </label>
                  <input
                    type="range"
                    className="physics-slider"
                    min="-300"
                    max="-5"
                    step="5"
                    value={chargeStrength}
                    onChange={(e) => setChargeStrength(Number(e.target.value))}
                    disabled={!physicsEnabled}
                  />
                  <div className="physics-slider-hints">
                    <span>Expand</span>
                    <span>Contract</span>
                  </div>
                </div>
                <div className="physics-option">
                  <label className="physics-slider-label">
                    <span>Label Spacing</span>
                    <span className="physics-slider-value">{collisionRadius === 0 ? 'Off' : collisionRadius}</span>
                  </label>
                  <input
                    type="range"
                    className="physics-slider"
                    min="0"
                    max="80"
                    step="5"
                    value={collisionRadius}
                    onChange={(e) => setCollisionRadius(Number(e.target.value))}
                    disabled={!physicsEnabled}
                  />
                  <div className="physics-slider-hints">
                    <span>Off</span>
                    <span>Max</span>
                  </div>
                </div>
                <div className="physics-option">
                  <label className="physics-slider-label">
                    <span>Island Pull</span>
                    <span className="physics-slider-value">{centerStrength.toFixed(2)}</span>
                  </label>
                  <input
                    type="range"
                    className="physics-slider"
                    min="0"
                    max="1"
                    step="0.05"
                    value={centerStrength}
                    onChange={(e) => setCenterStrength(Number(e.target.value))}
                    disabled={!physicsEnabled}
                  />
                  <div className="physics-slider-hints">
                    <span>Loose</span>
                    <span>Tight</span>
                  </div>
                </div>

                {/* Separator */}
                <div className="physics-panel-divider"></div>
                <div className="physics-panel-header">Edge Filter</div>
                <div className="physics-option">
                  <label className="physics-slider-label">
                    <span>Connection Strength</span>
                    <span className="physics-slider-value">{minLinkWeight}</span>
                  </label>
                  <input
                    type="range"
                    className="physics-slider"
                    min="1"
                    max={maxWeight}
                    step="1"
                    value={minLinkWeight}
                    onChange={(e) => setMinLinkWeight(Number(e.target.value))}
                  />
                  <div className="physics-slider-hints">
                    <span>All</span>
                    <span>{maxWeight}</span>
                  </div>
                  <div className="physics-slider-info">
                    <span><strong>{filteredGraphData.nodes.length}</strong> nodes</span>
                    <span><strong>{filteredGraphData.links.length}</strong> edges</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {onHelpSection && (
            <button
              className="help-info-icon"
              onClick={() => onHelpSection('network')}
              title="Network help"
            >
              ⓘ
            </button>
          )}
        </div>

        {/* Render ForceGraph2D only if dimensions are valid */}
        {dimensions.width > 0 && dimensions.height > 0 && (
          <ForceGraph2D
            ref={graphRef}
            width={dimensions.width}
            height={dimensions.height}
            graphData={filteredGraphData}
            nodeId="id"
            nodeCanvasObject={handleNodeCanvasObject} // Use updated node rendering
            // --- Link styling with highlighting ---
            linkWidth={getLinkWidth} // Use dynamic link width
            linkColor={getLinkColor} // Use dynamic link color
            linkDirectionalParticles={0}

            // --- Event handlers ---
            onNodeClick={(node, event) => {
              // Pass Ctrl/Cmd state for multi-select
              const isMultiSelect = event.ctrlKey || event.metaKey;
              if (onNodeSelect) onNodeSelect(node.id, isMultiSelect);
            }}
            // Handle background click to clear selection
            onBackgroundClick={() => {
                if (onNodeSelect) onNodeSelect(null, false); // Call handler with null to clear
            }}
            cooldownTicks={physicsEnabled ? Math.min(450, Math.round(150 + (filteredGraphData.nodes?.length || 0) * 0.2)) : 0}
            onEngineStop={() => {
              // Only auto-fit on initial load, not when a node is selected
              if (needsInitialFit.current && (!selectedNodeIds || selectedNodeIds.size === 0) && graphRef.current) {
                needsInitialFit.current = false;
                setTimeout(() => {
                  if (graphRef.current) {
                    graphRef.current.zoomToFit(400, 60);
                  }
                }, 100);
              }
            }}
          />
        )}

        {/* Industry/Facet Aura Legend — bottom-left, shown when overlay is active */}
        {showIndustryOverlay && activeIndustries.size > 0 && (
          <div className="industry-legend">
            <div className="industry-legend-header">
              {auraLabel} Aura
              {onHelpSection && (
                <button
                  className="help-info-icon industry-aura-help"
                  onClick={() => onHelpSection('industryAura')}
                  title="Learn about Industry Aura"
                >
                  ⓘ
                </button>
              )}
            </div>
            <div className="industry-opacity-control">
              <label className="industry-opacity-label">
                <span>Intensity</span>
                <span className="industry-opacity-value">{Math.round(industryOpacity * 100)}%</span>
              </label>
              <input
                type="range"
                className="industry-opacity-slider"
                min="0.02"
                max="1"
                step="0.02"
                value={industryOpacity}
                onChange={(e) => setIndustryOpacity(Number(e.target.value))}
              />
              <div className="industry-opacity-hints">
                <span>Subtle</span>
                <span>Solid</span>
              </div>
            </div>
            <div className="industry-opacity-control">
              <label className="industry-opacity-label">
                <span>Spread</span>
                <span className="industry-opacity-value">{auraSpread.toFixed(1)}×</span>
              </label>
              <input
                type="range"
                className="industry-opacity-slider"
                min="0.5"
                max="2"
                step="0.1"
                value={auraSpread}
                onChange={(e) => setAuraSpread(Number(e.target.value))}
              />
              <div className="industry-opacity-hints">
                <span>Tight</span>
                <span>Heatmap</span>
              </div>
            </div>
            {Object.entries(industryColorMap)
              .filter(([industry]) => activeIndustries.has(industry))
              .map(([industry, color]) => (
              <div className="industry-legend-item" key={industry}>
                <span
                  className="industry-legend-swatch"
                  style={{ backgroundColor: color }}
                />
                <span className="industry-legend-label">{industry}</span>
              </div>
            ))}
            <div className="industry-legend-hint">
              Intensity controls opacity, Spread fills gaps between nodes
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default NetworkGraph;